import { closest, ElementIntersectionObserver, toArray } from '@nrk/dh-basis-common'
import canvasSize from 'canvas-size'
import styles from './fargelegg.module.css'
import { debounce } from '../lib/utils.js'

const lineWidths = {
  1: 7,
  2: 15,
  3: 25,
}

// const CANVAS_MAX_AREA = 16777216

export default class Fargelegg extends HTMLElement {
  path = []
  image = new Image()
  _imageData
  noImageDataBug = false
  _dpr

  static get observedAttributes() {
    return ['mode', 'color']
  }

  connectedCallback() {
    const debouncedUpdateCanvasWidth = debounce(this._updateCanvasWidth, { delay: 500 })
    if (this._isFullWidth() || this._isFeatureCover()) {
      const images = toArray(this.querySelectorAll('img'))
      images.forEach((image) => {
        image.setAttribute('sizes', '100vw')
      })
    }
    if ('PointerEvent' in window) {
      document.body.addEventListener('pointerdown', this._handlePointerDown)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.addEventListener('touchstart', this._handlePointerDown)
      document.body.addEventListener('touchmove', this._preventScrollInOldTouchDevices, {
        passive: false,
      })
    } else {
      document.body.addEventListener('mousedown', this._handlePointerDown)
    }
    this.fargeleggContainer.addEventListener('click', this._handleClick)
    debouncedUpdateCanvasWidth()

    window.addEventListener('resize', debouncedUpdateCanvasWidth)
    this.querySelectorAll('img').forEach((img) =>
      img.addEventListener('load', debouncedUpdateCanvasWidth),
    )

    toArray(this.querySelectorAll('button[data-size]')).forEach((button) => {
      button.addEventListener('click', (e) => {
        this.size = e.target.getAttribute('data-size')
      })
    })

    toArray(this.querySelectorAll('button[data-color]')).forEach((button) => {
      button.addEventListener('click', (e) => {
        this.color = e.target.getAttribute('data-color')
      })
    })

    this.querySelector('button[data-download]').addEventListener('click', this._download)

    toArray(this.querySelectorAll('button[data-mode]')).forEach((button) => {
      button.addEventListener('click', (e) => {
        this.mode = e.target.getAttribute('data-mode')
      })
    })

    this._createIntersectionObserver()
  }

  disconnectedCallback() {
    if ('PointerEvent' in window) {
      document.body.removeEventListener('pointerdown', this._handlePointerDown)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.removeEventListener('touchstart', this._handlePointerDown)
      document.body.removeEventListener('touchmove', this._preventScrollInOldTouchDevices, {
        passive: false,
      })
    } else {
      document.body.removeEventListener('mousedown', this._handlePointerDown)
    }
    this._disconnectIntersectionObserver()
  }

  attributeChangedCallback(name) {
    switch (name) {
      case 'mode':
        this._updateMode()
        break
      case 'color':
        this._updateColor()
    }
  }

  _createIntersectionObserver = () => {
    this.observer = new ElementIntersectionObserver([this.menu], this._handleIntersectionChange, {
      threshold: [0.1, 0.9],
    })
  }

  _handleIntersectionChange = ([item]) => {
    if (item.isIntersecting && item.ratio > 0.9) {
      this.menuVisible = true
    } else {
      this.menuVisible = false
    }
  }

  _disconnectIntersectionObserver = () => {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  _preventScrollInOldTouchDevices = (e) => {
    if (!('PointerEvent' in window) && this.mode === 'paint') {
      e.preventDefault()
    }
  }

  _updateMode() {
    if (this.mode === 'paint') {
      this.fargeleggContainer.style.setProperty('touch-action', 'none')
    } else {
      this.fargeleggContainer.style.setProperty('touch-action', 'manipulation')
    }
  }

  _updateColor() {
    const currentColor = this.color
    toArray(this.querySelectorAll('button[data-color]')).forEach((button) => {
      const buttonColor = button.getAttribute('data-color')
      if (buttonColor === currentColor) {
        button.setAttribute('data-selected', '')
      } else {
        button.removeAttribute('data-selected')
      }
    })
  }

  _getDpr() {
    return this._dpr
  }

  _updateCanvasWidth = async () => {
    const canvas = this.drawingCanvas
    const drawing = this.drawing

    if (!drawing.complete) {
      return
    }

    const dpr = await findDPR(canvas.offsetWidth, canvas.offsetHeight, this.noImageDataBug)

    if (this._dpr === dpr && canvas.offsetWidth === this._currentCanvasWidth) {
      return
    }
    this._dpr = dpr

    canvas.width = canvas.offsetWidth * this._dpr
    canvas.height = canvas.offsetHeight * this._dpr
    this.drawingContext = canvas.getContext('2d')
    this.drawingContext.clearRect(0, 0, this.drawingCanvas.width, this.drawingCanvas.height)
    if (this.image && this.image.currentSrc) {
      this.drawingContext.drawImage(
        this.image,
        0,
        0,
        this.image.width,
        this.image.height,
        0,
        0,
        canvas.width,
        canvas.height,
      )
    }

    const tempCanvas = this.tempCanvas
    tempCanvas.width = tempCanvas.offsetWidth
    tempCanvas.height = tempCanvas.offsetHeight
    this.tempContext = tempCanvas.getContext('2d')
    this.tempContext.clearRect(0, 0, this.tempCanvas.width, this.tempCanvas.height)

    this._currentCanvasWidth = canvas.offsetWidth

    // Image data for fill function
    setTimeout(() => {
      let imageData
      try {
        imageData = this._getImageData()
      } catch (e) {
        // Newer versions of safari throws error when canvas is too big
        imageData = null
      }

      if (isValid(imageData)) {
        this._imageData = imageData
      } else if (!this.noImageDataBug) {
        // We test the size of the canvas.
        // However, sometimes the browser will refuse to write and read to the canvas.
        // We flag noImageDataBug and retry
        this.noImageDataBug = true
        setTimeout(this._updateCanvasWidth, 100)
      } else {
        // 2nd try, give up
        this._imageData = imageData
      }
    }, 100)
  }

  _isFullWidth() {
    const remoterendered = closest(this, '.remoterenderedcontent-reference')
    return remoterendered && remoterendered.classList.contains('fc')
  }

  _isFeatureCover() {
    const cover = closest(this, '.article-feature__cover')
    return !!cover
  }

  _handleClick = (e) => {
    e.preventDefault()

    const container = closest(e.target, `.${styles.fargeleggContainer}`)
    if (!container) {
      return
    }
    if (this.mode !== 'fill') {
      return
    }

    const { offsetX, offsetY } = e
    setTimeout(() => {
      this._fillArea(offsetX, offsetY)
    }, 0)
  }

  _handlePointerDown = (e) => {
    const container = closest(e.target, `.${styles.fargeleggContainer}`)
    if (!container) {
      return
    }
    if (this.mode !== 'paint') {
      return
    }
    e.preventDefault()
    e.stopImmediatePropagation()
    this.path = []
    if ('PointerEvent' in window) {
      document.body.addEventListener('pointermove', this._handlePointerMove)
      document.body.addEventListener('pointerup', this._handlePointerUp)
      document.body.addEventListener('pointercancel', this._handlePointerUp)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.addEventListener('touchmove', this._handlePointerMove, { passive: false })
      document.body.addEventListener('touchend', this._handlePointerUp)
      document.body.addEventListener('touchcancel', this._handlePointerUp)
    } else {
      document.body.addEventListener('mousemove', this._handlePointerMove)
      document.body.addEventListener('mouseup', this._handlePointerUp)
    }
    this._handlePointerMove(e)
    this._handlePointerMove(e)
  }

  _handlePointerMove = (e) => {
    e.preventDefault()
    e.stopImmediatePropagation()
    const container = closest(e.target, `.${styles.fargeleggContainer}`)
    let offsetX
    let offsetY
    if (!container) {
      offsetX = -1
      offsetY = -1
    } else if ('PointerEvent' in window) {
      offsetX = e.offsetX
      offsetY = e.offsetY
    } else if ('ontouchstart' in document.documentElement) {
      offsetX = e.targetTouches[0].pageX - container.offsetLeft
      offsetY = e.targetTouches[0].pageY - container.offsetTop
    } else {
      offsetX = e.offsetX
      offsetY = e.offsetY
    }
    this.path.push({ offsetX, offsetY })
    this._drawLine(this.tempContext, this.prevPos, { offsetX, offsetY }, 1)
    this.prevPos = { offsetX, offsetY }
  }

  _drawLine(ctx, prevPos, nextPos, dpr) {
    const innerWidth = Math.min(1200, this.offsetWidth)
    const lineWidth = lineWidths[this.size] + 0.005 * innerWidth
    const { width, height } = ctx.canvas

    ctx.save()
    ctx.strokeStyle = this.color
    ctx.lineWidth = lineWidth * dpr
    ctx.lineCap = 'round'

    if (
      prevPos &&
      nextPos &&
      prevPos.offsetX > 0 &&
      prevPos.offsetX < width &&
      prevPos.offsetY > 0 &&
      prevPos.offsetY < height &&
      nextPos.offsetX > 0 &&
      nextPos.offsetX < width &&
      nextPos.offsetY > 0 &&
      nextPos.offsetY < height
    ) {
      ctx.beginPath()
      ctx.moveTo(prevPos.offsetX * dpr, prevPos.offsetY * dpr)
      ctx.lineTo(nextPos.offsetX * dpr, nextPos.offsetY * dpr)
      ctx.stroke()
    }
    ctx.restore()
  }

  _handlePointerUp = () => {
    delete this.prevPos
    this.image.src = this.drawingCanvas.toDataURL()
    if ('PointerEvent' in window) {
      document.body.removeEventListener('pointermove', this._handlePointerMove)
      document.body.removeEventListener('pointerup', this._handlePointerUp)
      document.body.removeEventListener('pointercancel', this._handlePointerUp)
    } else if ('ontouchstart' in document.documentElement) {
      document.body.removeEventListener('touchmove', this._handlePointerMove, false)
      document.body.removeEventListener('touchend', this._handlePointerUp)
      document.body.removeEventListener('touchcancel', this._handlePointerUp)
    } else {
      document.body.removeEventListener('mousemove', this._handlePointerMove)
      document.body.removeEventListener('mouseup', this._handlePointerUp)
    }

    const dpr = this._getDpr()
    let prev
    this.path.forEach((pos) => {
      this._drawLine(this.drawingContext, prev, pos, dpr)
      prev = pos
    })
    this.tempContext.clearRect(0, 0, this.tempCanvas.width, this.tempCanvas.height)
  }

  _fillArea(x, y) {
    if (!this._imageData) {
      // Don't fill unless image is loaded and canvases are prepared
      return
    }
    const dpr = this._getDpr()
    const startX = Math.round(x * dpr)
    const startY = Math.round(y * dpr)
    const { width, height } = this.drawingCanvas
    const color = hexToRgbInt(this.color)
    const visited = new Uint8Array(width * height)

    const drawingData = this.drawingContext.getImageData(0, 0, width, height)
    const imageData = this._imageData
    const pixelData = {
      width: drawingData.width,
      height: drawingData.height,
      data: new Uint32Array(drawingData.data.buffer),
    }
    const startPos = startY * width + startX
    const pixelList = [startPos]
    const startColor = pixelData.data[startPos]
    const posOffset = [-width, width, -1, 1]
    const posOffsetX = [0, 0, -1, 1]
    if (color === startColor) {
      return
    }
    while (pixelList.length > 0) {
      const pos = pixelList.pop()
      const posX = pos % width
      if (!visited[pos] && imageData.data[pos * 4 + 3] < 200) {
        const pixel = pixelData.data[pos]
        if (pixel === startColor) {
          pixelData.data[pos] = color
          visited[pos] = 1
          for (let i = 0; i < posOffset.length; i++) {
            const nextPos = pos + posOffset[i]
            const nextPosX = posX + posOffsetX[i]
            if (nextPosX > -1 && nextPosX < width) {
              pixelList.push(nextPos)
            }
          }
        }
      }
    }
    this.drawingContext.putImageData(drawingData, 0, 0)
    setTimeout(() => {
      this.image.src = this.drawingCanvas.toDataURL()
    }, 100)
  }

  _download = () => {
    const drawingCanvas = this.drawingCanvas
    const { width, height } = drawingCanvas
    const link = document.createElement('a')
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    // Combine drawing and image
    const context = canvas.getContext('2d')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(this.image, 0, 0, width, height)
    context.drawImage(this.drawing, 0, 0, width, height)

    link.download = 'filename.png'
    link.href = canvas.toDataURL()
    link.target = '_blank'
    link.click()
  }

  _getImageData() {
    const { width, height } = this.drawingCanvas
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    context.drawImage(this.drawing, 0, 0, width, height)
    return context.getImageData(0, 0, width, height)
  }

  _toggleLock = () => {
    this.lock = !this.lock
  }

  get drawing() {
    return this.querySelector('img')
  }

  get fargeleggContainer() {
    return this.querySelector(`.${styles.fargeleggContainer}`)
  }

  get drawingCanvas() {
    return this.querySelector(`.${styles.drawingCanvas}`)
  }

  get tempCanvas() {
    return this.querySelector(`.${styles.tempCanvas}`)
  }

  get menu() {
    return this.querySelector(`.${styles.menu}`)
  }

  get size() {
    return this.getAttribute('size') || '2'
  }

  set size(val) {
    this.setAttribute('size', val)
  }

  get color() {
    return this.getAttribute('color') || '#6ab5e9'
  }

  set color(val) {
    this.setAttribute('color', val)
  }

  get mode() {
    return this.getAttribute('mode')
  }

  set mode(val) {
    if (val) {
      this.setAttribute('mode', val)
    } else {
      this.removeAttribute('mode')
    }
  }

  get menuVisible() {
    return this.hasAttribute('menu-visible')
  }

  set menuVisible(val) {
    if (val) {
      this.setAttribute('menu-visible', '')
    } else {
      this.removeAttribute('menu-visible')
    }
  }
}

function hexToRgbInt(rgbHex) {
  const hexString = `0x${rgbHex.replace('#', '').toUpperCase()}FF`
  const int = parseInt(hexString)
  const { r, g, b, a } = {
    r: (int >> 24) & 255,
    g: (int >> 16) & 255,
    b: (int >> 8) & 255,
    a: int & 255,
  }
  return (a << 24) + (b << 16) + (g << 8) + r
}

function findDPR(offsetWidth, offsetHeight, noImageDataBug) {
  if (noImageDataBug) {
    return Promise.resolve(1)
  }
  const sizes = Array.from({ length: window.devicePixelRatio })
    .map((_, idx) => idx + 1)
    .map((dpr) => [offsetWidth * dpr, offsetHeight * dpr])
  sizes.reverse()

  return new Promise((resolve) => {
    canvasSize.test({
      sizes,
      onError(notAllowedWidth) {
        if (notAllowedWidth === offsetWidth) {
          resolve(1)
        }
        // Do nothing
      },
      onSuccess(allowedWidth) {
        const dpr = Math.floor(allowedWidth / offsetWidth)
        resolve(dpr)
      },
    })
  })
}

function isValid(imageData) {
  if (!imageData) {
    return false
  }
  let valid = false
  for (let i = 0; i < imageData.data.length; i++) {
    if (imageData.data[i] > 0) {
      valid = true
      break
    }
  }
  return valid
}
